import React from 'react';
import {useNavigate, useParams} from 'react-router';

import useCategoriesWithActions from '../Queries/useCategoriesWithActions';
import {categoryIcons} from '../Util/constants';
import {findByRouteKey, useCanonicalRouteParams} from '../Util/routeIdentifier';
import ProItemCard from './Components/ProItemCard';
import {RESOURCE_GRID_CLASSES, ResourceNotice, ResourceQueryState} from './LoadingCard';
import {RESOURCE_TITLE_CLASSES, ResourceBreadcrumbs} from './ProCategories';

// `group_id` is nullable, so an action without a group still needs a heading.
const UNGROUPED_HEADING = 'Other resources';

const ProParentViewCategory = () => {
  const {data: categories, error, refetch} = useCategoriesWithActions();
  const {category: categoryKey} = useParams();
  const navigate = useNavigate();

  // Found by its slug, or by the numeric id of an address from before COM-477, which
  // becomes the slug one in place.
  const category = findByRouteKey(categories, categoryKey);
  useCanonicalRouteParams({category: category?.slug}, {resource: 'category'});

  // Key actions grouped by group name, in the order each group first appears.
  const keyActionsByGroup = new Map();
  (category?.uniqueKeyActions ?? []).forEach((keyAction) => {
    const groupName = keyAction.group?.name ?? UNGROUPED_HEADING;
    const keyActionWithIcon = {...keyAction, icon: categoryIcons[category.name]};

    keyActionsByGroup.set(groupName, [
      ...(keyActionsByGroup.get(groupName) ?? []),
      keyActionWithIcon,
    ]);
  });

  return (
    <div data-testid="pro-parent-category">
      <ResourceBreadcrumbs
        items={[
          {label: 'Home', to: '/dashboard'},
          {label: 'Parents', to: '/parent-categories'},
          ...(category ? [{label: category.name}] : []),
        ]}
      />
      <h1 className={`mb-6 ${RESOURCE_TITLE_CLASSES}`}>{category?.name ?? 'Parents'}</h1>
      <ResourceQueryState
        data={categories}
        error={error}
        refetch={refetch}
        label="parent resources"
        placeholderCount={9}>
        {!category ? (
          <ResourceNotice>Category not found.</ResourceNotice>
        ) : keyActionsByGroup.size === 0 ? (
          <ResourceNotice>No resources are available in this category yet.</ResourceNotice>
        ) : (
          <div className="flex flex-col gap-8">
            {[...keyActionsByGroup].map(([groupName, keyActions]) => (
              <section
                key={groupName}
                className="border-b border-(--compass-border) pb-8 last:border-b-0 last:pb-0">
                {/* Level three is the heading the Professional journey selects. */}
                <h3 className="mb-4 text-xl font-semibold break-words">{groupName}</h3>
                <ul className={RESOURCE_GRID_CLASSES}>
                  {keyActions.map((keyAction) => (
                    <li key={`key-action${keyAction.id}`} className="min-w-0">
                      <ProItemCard
                        item={keyAction}
                        onClick={() => navigate(`item/${encodeURIComponent(keyAction.slug)}`)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </ResourceQueryState>
    </div>
  );
};

export default ProParentViewCategory;
